import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Loader2, ShieldX } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1696542095330-ecda668b21d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxuZmwlMjBmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3ODY0NzY5NDd8MA&ixlib=rb-4.1.0&q=85";

export default function Join() {
  const { code } = useParams();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [valid, setValid] = useState(null); // null=checking
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invite/${code}`).then((res) => setValid(res.data.valid)).catch(() => setValid(false));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(username, password);
      navigate("/picks");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={HERO} alt="stadium" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-0 left-0 p-12 z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#E31837] flex items-center justify-center">
              <Trophy className="w-6 h-6" />
            </div>
            <span className="font-['Bebas_Neue'] text-4xl tracking-wide">PICK'EM POOL</span>
          </div>
          <h1 className="font-['Bebas_Neue'] text-6xl leading-none tracking-tight max-w-md">
            YOU'RE INVITED.<br />PICK TO WIN.
          </h1>
          <p className="text-white/60 mt-4 max-w-sm">
            Join the season-long NFL pick'em pool. Create your account and lock in your Week 1 picks.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#E31837] flex items-center justify-center">
              <Trophy className="w-5 h-5" />
            </div>
            <span className="font-['Bebas_Neue'] text-2xl tracking-wide">PICK'EM POOL</span>
          </div>

          {valid === null ? (
            <div className="flex items-center gap-2 text-white/50 py-20">
              <Loader2 className="w-5 h-5 animate-spin" /> Checking your invite…
            </div>
          ) : !valid ? (
            <div className="text-center py-10" data-testid="invite-invalid">
              <ShieldX className="w-10 h-10 text-[#EF4444] mx-auto mb-3" />
              <h2 className="font-['Bebas_Neue'] text-4xl tracking-tight">INVITE EXPIRED</h2>
              <p className="text-white/50 text-sm mt-2">
                This invite link is no longer active. Ask the commissioner for a fresh link.
              </p>
              <Link to="/login" className="inline-block mt-6 text-[#E31837] uppercase text-xs tracking-widest hover:underline" data-testid="invite-to-login">
                Go to Sign In →
              </Link>
            </div>
          ) : (
            <>
              <div className="inline-block mb-4 text-[10px] uppercase tracking-[0.25em] text-[#22C55E] border border-[#22C55E]/40 px-2 py-1" data-testid="invite-valid">
                ✓ Valid invite
              </div>
              <h2 className="font-['Bebas_Neue'] text-4xl tracking-tight mb-1">JOIN THE POOL</h2>
              <p className="text-white/50 text-sm mb-6">Pick a username and password to get started.</p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/50">Username</label>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="mt-1 w-full bg-[#141414] border border-white/10 px-4 py-3 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none focus:border-transparent transition-colors duration-200"
                    placeholder="e.g. gridiron_greg"
                    data-testid="join-username-input"
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-white/50">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full bg-[#141414] border border-white/10 px-4 py-3 rounded-sm focus:ring-2 focus:ring-[#E31837] focus:outline-none focus:border-transparent transition-colors duration-200"
                    placeholder="••••••••"
                    data-testid="join-password-input"
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-3 py-2" data-testid="join-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E31837] hover:bg-[#C8102E] py-3 rounded-sm uppercase tracking-[0.2em] text-sm font-semibold transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="join-submit-button"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Join The Pool
                </button>
              </form>

              <p className="text-white/40 text-xs mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-[#E31837] hover:underline">Sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
